"use client";

import { useEffect } from "react";
import { classifyAiReferral } from "@/lib/aiSources";

/**
 * Reports a visit that arrived from an AI answer engine, once per session.
 *
 * Renders nothing. Mounted in the root layout so it covers every marketing page.
 *
 * Why the browser and not the server: every content page here is static/ISR behind the Cloudflare
 * edge, so a cache HIT never reaches origin and there is no per-request place to read the `Referer`
 * header. `document.referrer` is the only vantage point that sees all traffic.
 *
 * Fires at most ONCE per tab session, and only when the visit actually looks like an AI referral --
 * ordinary traffic sends nothing at all, so this adds no request to the overwhelming majority of
 * pageviews.
 */

const SESSION_KEY = "hb_ai_ref_sent";

export function AiReferralBeacon() {
  useEffect(() => {
    // The referrer only describes the ENTRY to this tab. Once recorded, client-side navigations
    // would re-report the same source against a different path, inflating the count.
    let alreadySent = false;
    try {
      alreadySent = sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      // Private mode / storage disabled: fall through and just skip the dedupe.
    }
    if (alreadySent) return;

    const hit = classifyAiReferral(document.referrer, window.location.href);
    if (!hit) return; // ordinary visit -- send nothing

    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* not fatal */
    }

    const payload = JSON.stringify({
      source: hit.source,
      via: hit.via,
      label: hit.label,
      evidence: hit.evidence,
      // Path only -- never the full URL, which could carry query strings we have no reason to log.
      path: window.location.pathname,
    });

    // keepalive so the request survives the user navigating away immediately. Failure is silently
    // ignored on purpose: a missed metric must never surface as a console error to a visitor.
    void fetch("/api/ai-referral", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  }, []);

  return null;
}
